import { Link } from 'react-router-dom';

/**
 * Setting up a barcode scanner to read a driver's licence.
 *
 * Scanning the PDF417 on the back of a licence fills most of the demographic form, and it only works if
 * the scanner is configured for it. Two settings matter, and one of them is not obvious:
 *
 *  - PDF417 has to be enabled. Many scanners ship with only 1D symbologies on.
 *  - The barcode's own LINE ENDINGS have to be transmitted. AAMVA separates each data element with a
 *    line feed, `parseAamva` splits on those, and a scanner that strips control characters — which is a
 *    common default — delivers one long line that parses to nothing. Nothing on screen distinguishes
 *    that from a bad scan, which is what made this worth its own page rather than a line of small print.
 *
 * The Teemi barcodes are the images v1 shipped, carried across unchanged. They are configuration
 * commands for that hardware and are not something to redraw or regenerate: a wrong one reconfigures a
 * customer's scanner into a state they then have to undo.
 */

/** Models the Teemi barcodes below are documented to work with. From v1's own list. */
const TEEMI_MODELS = [
  'T22N', 'T26', 'TMSL-54', 'TMSL-55', 'TMSL-56', 'TMSL-57',
  'TMSL-60', 'TMSL-70', 'TMSL-72', 'TMSL-73', 'TMSL-74',
];

const STEPS = [
  {
    img: 'teemi-enablepdf417.png',
    title: '1. Enable PDF417',
    body: 'Turns on the symbology a driver’s licence uses. Most scanners ship with it off.',
  },
  {
    img: 'teemi-datamatrix.png',
    title: '2. Enable Data Matrix',
    body: 'Some jurisdictions and military IDs use Data Matrix rather than PDF417. Harmless to enable either way.',
  },
  {
    img: 'teemi-dataformatting.png',
    title: '3. Transmit line endings',
    body: 'The important one. It tells the scanner to send the line feeds inside the barcode instead of stripping them — without this the licence data arrives as a single line and nothing is filled in.',
  },
];

export function BarcodeScanner() {
  return (
    <div className="stack barcode-help">
      <div className="panel">
        <div className="panel-head">
          <h1 style={{ margin: 0 }}>Barcode scanner setup</h1>
        </div>
        <p>
          You can fill most of the demographic form by scanning the 2D barcode on the back of a
          driver&rsquo;s licence. Scan it at any point while the <strong>Demographics</strong> step is open
          &mdash; there is no button to press and no field to focus first.
        </p>
        <p className="field-help">
          Your scanner needs to behave as a keyboard (the usual default, sometimes called HID or
          keyboard-wedge mode), have <strong>PDF417</strong> enabled, and <strong>transmit the line
          endings</strong> contained in the barcode. That last one is the setting people miss: a licence
          barcode separates each piece of information with a line feed, and a scanner that strips those
          sends one long line that cannot be read apart.
        </p>
      </div>

      <div className="panel">
        <div className="section-title" style={{ marginTop: 0 }}>Teemi scanners</div>
        <p>
          Scan these three barcodes in order with the scanner you want to configure. Each one is a
          configuration command &mdash; the scanner will beep to confirm it took effect.
        </p>
        <p className="field-help">
          Works with Teemi {TEEMI_MODELS.slice(0, -1).join(', ')} and {TEEMI_MODELS.at(-1)}. If you have a
          different make, follow the general instructions below instead.
        </p>
        <div className="bc-steps">
          {STEPS.map((s) => (
            <div className="bc-step" key={s.img}>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
              {/*
                * Served from /public rather than bundled: these are printed and scanned off a screen, so
                * they must render at full resolution and be openable on their own for a bigger target.
                */}
              <a href={`/barcodes/${s.img}`} target="_blank" rel="noreferrer" title="Open full size">
                <img src={`/barcodes/${s.img}`} alt={s.title} />
              </a>
            </div>
          ))}
        </div>
        <p className="field-help">
          Scanning off a screen can be unreliable on some models. If a barcode will not read, open it full
          size with the link, zoom in, or print the page.
        </p>
      </div>

      <div className="panel">
        <div className="section-title" style={{ marginTop: 0 }}>Any other scanner</div>
        <p>
          Any scanner that reads PDF417 and can send control characters will work. The wording differs
          between manufacturers, so look for these three things in your scanner&rsquo;s manual or
          configuration utility:
        </p>
        <ol className="bc-generic">
          <li>
            <strong>Interface: USB HID keyboard.</strong> Also called keyboard wedge or keyboard emulation.
            Not USB-COM, serial or POS mode &mdash; the app reads the scanner as if someone were typing.
          </li>
          <li>
            <strong>Enable PDF417.</strong> Under symbologies, 2D codes, or barcode types. Enabling Data
            Matrix and QR at the same time does no harm and covers the ID formats that use them.
          </li>
          <li>
            <strong>Transmit control characters / line feeds.</strong> Look for &ldquo;data
            formatting&rdquo;, &ldquo;transmit AIM ID&rdquo;, &ldquo;send control characters&rdquo;,
            &ldquo;GS/LF substitution&rdquo; or a terminator setting. The scanner must pass through the
            line feeds inside the barcode rather than removing or replacing them.
          </li>
        </ol>
        <p className="field-help">
          A suffix or terminator of CR, LF or CR+LF on the end of the scan is fine either way &mdash; the
          app tolerates all three. It is the line endings <em>inside</em> the data that matter.
        </p>
      </div>

      <div className="panel">
        <div className="section-title" style={{ marginTop: 0 }}>Checking it works</div>
        <ol className="bc-generic">
          <li>Start a transaction and open the <strong>Demographics</strong> step.</li>
          <li>Scan the back of a driver&rsquo;s licence. Do not click into a field first.</li>
          <li>
            The name, date of birth, sex, height, eye colour and address should fill in. Anything the
            workflow does not ask for is ignored.
          </li>
        </ol>
        <p className="field-help">
          If a single field fills with a long run of characters, the scanner is stripping line endings
          &mdash; step 3 above is the one to revisit. If nothing happens at all, the scanner is probably
          not in keyboard mode, or PDF417 is still off.
        </p>
        <div style={{ marginTop: 12 }}>
          <Link to="/app/help" className="track-link">&larr; Back to help &amp; support</Link>
        </div>
      </div>
    </div>
  );
}
